// The main menu as a LANDING PAGE (D221, `design_handoff_main_menu`): a cinematic hero over
// a still of the real board, a three-step CTA stack, the proof band, and the utility footer.
//
// It replaces the single centred column the menu had been since D157. The reason is the
// handoff's own: this screen is the product's front door and its only conversion surface, and
// a flat button list neither shows the game nor asks for the account. What it must NOT do is
// lose anything the flat version shipped — the D152 rule that a handoff is a visual language
// and not a licence to delete features. Two things the handoff simply omits are kept here on
// purpose: the name chip stays INLINE-EDITABLE (its footer pill only signs in), and the
// master mute stays in the footer (H12 shipped it; the handoff's footer has no sound at all).
//
// House rules: injected providers only, every string via textContent (T-SEC-006), no default
// export, no `innerHTML` anywhere.

import { SERVER_CONFIG } from '@kolony/shared';
import { sealMark, svgEl, wordmarkLockup } from './kit';
import { createUtilityBar } from './homeFooter';
import { pauseWhileOffscreen } from './offscreenAnimations';
import { createRankRail } from './rankRail';
import { createProofBar, createTestimonials, type RatingAggregate, type Testimonial } from './landing';
import type { RankView } from './rankInfo';
import type { ActivityStat } from '../api/stats';

/** The room-code shape, read from config rather than restated (invariant 6). */
const ROOM_CODE = SERVER_CONFIG.net.roomCode;

/** The coin's box in the hero. theme.css steps it down on short viewports. */
const SEAL_PX = 118;

/**
 * The five resource renders that drift behind the hero — the real shipped icons (D103's
 * models, rendered), not the handoff's extracted crops. Positions and sizes are the
 * handoff's; the bob periods are deliberately coprime-ish so they never fall into sync.
 */
/**
 * The handoff's five floating resource renders (§1.4).
 *
 * WHERE and HOW BIG each one is lives in the stylesheet, keyed by resource, not in an inline
 * style here (D237). Inline styles cannot be overridden by a media query without `!important`,
 * and the positions are percentages of a WIDE hero — so the first cut had no way to move them
 * out of the column's path on a narrow screen and simply DELETED them below 900px. That is
 * most of a tablet, and it is what the user noticed. All this table carries now is which
 * render, which of the two bob cycles, and how long that cycle takes — the staggered periods
 * the handoff asks for so they never sync.
 */
const FLOATERS: ReadonlyArray<{ src: string; bob: 1 | 2; secs: number }> = [
  { src: 'wool', bob: 1, secs: 7 },
  { src: 'timber', bob: 2, secs: 9 },
  { src: 'grain', bob: 2, secs: 8 },
  { src: 'brick', bob: 1, secs: 6.5 },
  { src: 'ore', bob: 1, secs: 8.5 },
];

/** What the one-tap lane says and does. */
export interface PlayAgainOffer {
  title: string;
  sub: string;
}

export interface HomeScreenOpts {
  doc: Document;
  playerName: { get(): string; set(v: string): void };
  signedIn: () => boolean;
  /** Primary CTA: create an online room (H3 wires the real flow). */
  onPlay(): void;
  /**
   * D216: open the Play panel, optionally on a named tab. The panel owns every choice the
   * front door offers — which lane, which pace, which table size.
   *
   * It deliberately does NOT start anything: Play is two taps, and the second one is the one
   * that costs something.
   */
  onOpenPlay(): void;
  /** The remembered selection, worn by the Play button so the menu says what it will do. */
  playSummary?(): string;
  /**
   * D221: the ONE-TAP lane. Play stays the configurable two-tap door; this button starts the
   * player's last game outright, so the two are deliberately different promises rather than
   * two routes to the same panel.
   */
  onPlayAgain?(): void;
  /** Its two lines — "Play again" + the exact last config, or the first-timer's offer. */
  playAgainOffer?(): PlayAgainOffer;
  /** D216: join a room by its four-character code — the wire has always had them. */
  onJoinCode?(code: string): void;
  onSignIn(): void;
  /** D163: open the feedback box (overlays home). */
  onFeedback(): void;
  /**
   * D187: open Settings. Optional, and the entry renders only when supplied, so the door does
   * not exist in a build that does not want it.
   */
  onSettings?(): void;
  /** H10-S6b: opens the Friends hub. Omitted ⇒ no Friends entry renders at all. */
  onFriends?(): void;
  /** D248: the operator's dashboard, drawn in the utility bar only for an admin account. */
  admin?: { visible(): boolean; open(): void };
  /** D250/A2: How to play was tapped. */
  onHowTo?(): void;
  /**
   * D250/A2: the page was scrolled past the fold, reported ONCE per mount.
   *
   * The evidence band, the testimonial band and the closing call to action all live below
   * it (D221/D238), and until now nothing said whether anybody ever got there — the whole
   * landing page was unmeasured investment.
   */
  onScrolledPastFold?(): void;
  /**
   * H14: the player's rank, for the rail in the hero. Omitted ⇒ no rail at all. `subscribe`
   * lets the rail redraw when a settle or a claim lands without the menu having to poll.
   */
  rank?: {
    current(): RankView;
    subscribe(fn: (v: RankView) => void): () => void;
    refresh(): void;
    /** D218: the rail is the thing a returning player looks at — make it a way in. */
    onOpenLadder?(): void;
  };
  /** H12 audio: the master mute. Omitted ⇒ no sound control renders. */
  audio?: {
    isMuted(): boolean;
    setMuted(muted: boolean): void;
    onMuteChange(handler: () => void): () => void;
  };
  /** D221: the proof band's numbers. Omitted ⇒ the band never renders. */
  activity?(): ActivityStat | null;
  /** D221: real, consented quotes. There is no source of these yet — see ui/landing.ts. */
  testimonials?: readonly Testimonial[];
  ratingAggregate?: RatingAggregate | null;
  /** Honour the player's own reduced-motion choice as well as the OS setting. */
  reducedMotion?(): boolean;
}

export interface HomeScreen {
  readonly root: HTMLElement;
  /**
   * (Re)render the dynamic parts — name, auth zone, offers — and BRING THE SCREEN TO THE
   * FRONT. Only a flow returning to the menu calls this: the screen is a fixed layer above the
   * lobby and the whole game screen, so a `show()` while a game owns the page buries the game.
   */
  show(): void;
  hide(): void;
  /**
   * D255: re-render the same dynamic parts IN PLACE, never changing whether the screen is
   * showing. For anything that merely learned something new — an account refresh settling, a
   * sign-in, a sign-out — which can happen while a lobby or a board is up.
   */
  refresh(): void;
  /** H10-S6b: waiting-invite count on the Friends entry; 0 clears it. */
  setFriendsBadge(n: number): void;
  /** D216: repaint the Play button's subtitle after the panel writes a new selection. */
  refreshPlaySummary(): void;
  /** D221: the proof band, once the stats fetch lands. */
  refreshActivity(): void;
}

export function mountHomeScreen(opts: HomeScreenOpts): HomeScreen {
  const d = opts.doc;
  const root = d.createElement('div');
  root.className = 'kolony-home kolony-home--hud';

  // ── hero ────────────────────────────────────────────────────────────────────
  const hero = d.createElement('section');
  hero.className = 'home-hero';
  const glow = d.createElement('div');
  glow.className = 'hero-glow';
  const horizon = d.createElement('div');
  horizon.className = 'hero-horizon'; // a STILL of the real board (tools/capture-hero)
  horizon.dataset['heroHorizon'] = '1';
  const fade = d.createElement('div');
  fade.className = 'hero-fade';
  hero.append(glow, horizon, fade);

  // D294: everything on this page that animates forever, paused while scrolled out of view
  const endless: Element[] = [];
  for (const f of FLOATERS) {
    const img = d.createElement('img');
    endless.push(img);
    img.className = `hero-float hero-float--${f.src} hero-float--bob${String(f.bob)}`;
    img.dataset['heroFloat'] = f.src;
    img.src = `/assets/icons/resources/${f.src}.png`;
    img.alt = '';
    img.decoding = 'async';
    img.loading = 'lazy';
    img.style.cssText = `animation-duration:${String(f.secs)}s`;
    hero.append(img);
  }

  const col = d.createElement('div');
  col.className = 'kolony-home-col hero-col';
  const seal = sealMark(d, SEAL_PX);
  seal.classList.add('hero-coin');
  endless.push(seal);
  const wordmark = wordmarkLockup(d, 'centred');
  wordmark.classList.add('kolony-wordmark--hud');
  col.append(seal, wordmark);

  // ── the rank rail ───────────────────────────────────────────────────────────
  const rail = opts.rank ? createRankRail(d) : null;
  let unsubscribeRank: (() => void) | null = null;
  if (rail && opts.rank) {
    rail.update(opts.rank.current());
    unsubscribeRank = opts.rank.subscribe((v) => rail.update(v));
    // D218: given somewhere to go, the rail becomes a real control — a div that answers
    // clicks is not a button to anyone using a keyboard.
    const openLadder = opts.rank.onOpenLadder;
    if (openLadder) {
      rail.root.classList.add('rank-rail--tappable');
      rail.root.setAttribute('role', 'button');
      rail.root.tabIndex = 0;
      rail.root.setAttribute('aria-label', 'Open the leaderboard');
      rail.root.addEventListener('click', () => openLadder());
      rail.root.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === 'Enter' || k === ' ') {
          e.preventDefault();
          openLadder();
        }
      });
    }
    col.append(rail.root);
  }

  // ── the CTA stack ───────────────────────────────────────────────────────────
  const actions = d.createElement('div');
  actions.className = 'menu-actions';

  // Play: the configurable lane. It does NOT use a hiding helper, for the same reason the
  // solo button never did — the screen must survive until a card is actually chosen.
  const playBtn = d.createElement('button');
  playBtn.type = 'button';
  playBtn.className = 'menu-btn menu-btn--play';
  playBtn.dataset['homeQuickmatch'] = '1'; // hook kept: the suites address this by ROLE
  playBtn.setAttribute('aria-haspopup', 'dialog');
  const shimmer = d.createElement('span');
  shimmer.className = 'menu-btn-shimmer';
  const playLabel = d.createElement('span');
  playLabel.className = 'menu-btn-label';
  playLabel.textContent = 'Play';
  const playNote = d.createElement('span');
  playNote.className = 'menu-btn-note';
  playNote.dataset['playSummary'] = '1';
  playBtn.append(shimmer, playLabel, playNote);
  endless.push(playBtn); // the sheen is its child: the button's visibility is the sheen's
  pauseWhileOffscreen(endless);
  playBtn.addEventListener('click', () => opts.onOpenPlay());

  // Play again: the ONE-TAP lane. This one really does start a game.
  const againBtn = d.createElement('button');
  againBtn.type = 'button';
  againBtn.className = 'menu-btn menu-btn--again';
  againBtn.dataset['homeAgain'] = '1';
  const tri = svgEl<SVGSVGElement>(d, 'svg', { width: 15, height: 16, viewBox: '0 0 15 16', 'aria-hidden': 'true' });
  tri.append(svgEl(d, 'path', { d: 'M2 2 L13 8 L2 14 Z', fill: 'currentColor' }));
  const againText = d.createElement('span');
  againText.className = 'menu-again-text';
  const againTitle = d.createElement('span');
  againTitle.className = 'menu-again-title';
  const againSub = d.createElement('span');
  againSub.className = 'menu-again-sub';
  againText.append(againTitle, againSub);
  againBtn.append(tri, againText);
  againBtn.addEventListener('click', () => {
    hide();
    opts.onPlayAgain?.();
  });

  const createBtn = d.createElement('button');
  createBtn.type = 'button';
  createBtn.className = 'menu-btn menu-btn--create';
  createBtn.dataset['homeCreate'] = '1';
  createBtn.textContent = 'Create a room';
  createBtn.addEventListener('click', () => {
    hide();
    opts.onPlay();
  });

  // D216: the code IS the invitation, and until D216 it could only be pasted as a URL.
  const joinRow = d.createElement('div');
  joinRow.className = 'menu-join';
  if (opts.onJoinCode) {
    const onJoinCode = opts.onJoinCode;
    const joinLabel = d.createElement('span');
    joinLabel.className = 'menu-join-label';
    joinLabel.textContent = 'Have a code?';
    const codeInput = d.createElement('input');
    codeInput.className = 'menu-join-input';
    codeInput.dataset['joinCode'] = '1';
    codeInput.maxLength = ROOM_CODE.length;
    codeInput.setAttribute('aria-label', 'Room code');
    codeInput.placeholder = 'ABCD';
    const joinGo = d.createElement('button');
    joinGo.type = 'button';
    joinGo.className = 'menu-join-go';
    joinGo.dataset['joinGo'] = '1';
    joinGo.textContent = 'Join';
    // D190: a control must never offer an action the server will refuse. The code is four
    // characters over a fixed alphabet, so Join stays disabled until it is one.
    const normalise = (raw: string): string =>
      raw
        .toUpperCase()
        .split('')
        .filter((ch) => ROOM_CODE.alphabet.includes(ch))
        .join('')
        .slice(0, ROOM_CODE.length);
    const sync = (): void => {
      const v = normalise(codeInput.value);
      if (codeInput.value !== v) codeInput.value = v;
      const ready = v.length === ROOM_CODE.length;
      joinGo.disabled = !ready;
      // the handoff's one interaction reward here: it ignites the moment the code completes
      joinGo.classList.toggle('menu-join-go--ready', ready);
    };
    const go = (): void => {
      const v = normalise(codeInput.value);
      if (v.length !== ROOM_CODE.length) return;
      hide();
      onJoinCode(v);
    };
    codeInput.addEventListener('input', sync);
    codeInput.addEventListener('keydown', (e) => {
      if ((e as KeyboardEvent).key === 'Enter') go();
    });
    joinGo.addEventListener('click', go);
    sync();
    joinRow.append(joinLabel, codeInput, joinGo);
  }

  actions.append(playBtn, againBtn, createBtn);
  col.append(actions);

  // ── the claim button ────────────────────────────────────────────────────────
  //
  // D238 (the user's direction): ONE sign-up control on the page, not two. The hero used to
  // carry a gold-OUTLINED pill ("Sign up free — keep your rank everywhere") and the bottom of
  // the page a solid gold CTA ("Claim your settler name — it's free") — the same offer twice,
  // in two voices, one of them quiet enough to read as a notice rather than a button. The
  // solid one wins and moves up here; the outlined one is gone. It keeps the `menu-signup`
  // hooks because it is the same control doing the same job: it converts on the guest-rank
  // pain point (D200), it never blocks play, and a signed-in player is offered nothing.
  const signUp = d.createElement('button');
  signUp.type = 'button';
  signUp.className = 'menu-signup';
  signUp.dataset['homeSignup'] = '1';
  const shield = svgEl<SVGSVGElement>(d, 'svg', { width: 16, height: 18, viewBox: '0 0 18 20', 'aria-hidden': 'true' });
  shield.append(svgEl(d, 'path', { d: 'M9 1 L17 4 V10 C17 15 13.5 18.2 9 19.5 C4.5 18.2 1 15 1 10 V4 Z', fill: 'currentColor' }));
  const signUpText = d.createElement('span');
  signUpText.className = 'menu-signup-text';
  const signUpTop = d.createElement('span');
  signUpTop.className = 'menu-signup-top';
  signUpTop.textContent = 'Claim your settler name';
  const signUpSub = d.createElement('span');
  signUpSub.className = 'menu-signup-sub';
  signUpSub.textContent = 'and keep your rank';
  signUpText.append(signUpTop, signUpSub);
  signUp.append(shield, signUpText);
  signUp.addEventListener('click', () => opts.onSignIn());

  col.append(signUp);
  hero.append(col);

  const cue = d.createElement('div');
  cue.className = 'hero-cue';
  cue.dataset['heroCue'] = '1';
  const cueText = d.createElement('span');
  cueText.textContent = 'THE ISLAND AWAITS';
  const chev = svgEl<SVGSVGElement>(d, 'svg', { width: 14, height: 8, viewBox: '0 0 14 8', 'aria-hidden': 'true' });
  chev.append(svgEl(d, 'path', { d: 'M1 1 L7 7 L13 1', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round' }));
  cue.append(cueText, chev);
  hero.append(cue);
  root.append(hero);

  // ── the evidence band, and the band that has no evidence yet ────────────────
  const proof = createProofBar(d, { ...(opts.reducedMotion ? { reducedMotion: opts.reducedMotion } : {}) });
  root.append(proof.root);
  const testimonials = createTestimonials({
    doc: d,
    ...(opts.testimonials ? { quotes: opts.testimonials } : {}),
    aggregate: opts.ratingAggregate ?? null,
    // D238: the join row CLOSES the page now — the slot the claim button vacated. It was
    // sitting under the three play buttons, where it competed with them for a decision it is
    // not part of: a code is something you were sent, not something you choose between.
    ...(opts.onJoinCode ? { closing: joinRow } : {}),
  });
  root.append(testimonials.root);

  // ── the utility footer ──────────────────────────────────────────────────────
  const footer = createUtilityBar({
    doc: d,
    playerName: opts.playerName,
    signedIn: () => opts.signedIn(),
    onSignIn: () => opts.onSignIn(),
    onFeedback: () => opts.onFeedback(),
    ...(opts.onSettings ? { onSettings: opts.onSettings } : {}),
    ...(opts.onFriends ? { onFriends: opts.onFriends } : {}),
    ...(opts.admin ? { admin: opts.admin } : {}),
    ...(opts.onHowTo ? { onHowTo: opts.onHowTo } : {}),
    ...(opts.audio ? { audio: opts.audio } : {}),
  });
  root.append(footer.root);
  d.body.appendChild(root);

  /**
   * D250/A2: the fold crossing, once per mount. Passive listener, armed only when somebody
   * is listening.
   *
   * THE TARGET IS THE LESSER OF HALF A SCREEN AND MOST OF THE SCROLL THERE ACTUALLY IS, and
   * that second term is not defensive coding — it is a bug the browser caught. The first cut
   * asked for half a viewport height, and on a short window the whole page scrolls less than
   * that: measured live at 720 CSS px, the landing page is 938 tall, so its entire scroll
   * range is 218 px and a 360 px target could never be reached. Every reader of that page
   * would have been recorded as never reaching the band they were looking straight at.
   */
  const FOLD_SCREEN = 0.5; // half a screen…
  const FOLD_RANGE = 0.6; // …or most of whatever there is to scroll, whichever comes first
  let foldReported = false;
  if (opts.onScrolledPastFold) {
    root.addEventListener(
      'scroll',
      () => {
        if (foldReported || root.scrollTop <= 0) return; // a scroll event at rest is not a read
        const target = Math.min(FOLD_SCREEN * root.clientHeight, FOLD_RANGE * Math.max(0, root.scrollHeight - root.clientHeight));
        if (root.scrollTop < target) return;
        foldReported = true;
        opts.onScrolledPastFold?.();
      },
      { passive: true },
    );
  }

  const refreshPlaySummary = (): void => {
    playNote.textContent = opts.playSummary?.() ?? '';
  };
  const refreshAgain = (): void => {
    const offer = opts.playAgainOffer?.() ?? null;
    againBtn.hidden = offer === null || opts.onPlayAgain === undefined;
    againTitle.textContent = offer?.title ?? '';
    againSub.textContent = offer?.sub ?? '';
  };
  const refreshActivity = (): void => proof.update(opts.activity?.() ?? null);
  const refreshSignUp = (): void => {
    // D238: the band's closing CTA was the same offer and went stale with it (D222). The
    // offer lives only here now, so there is nothing downstream to refresh.
    signUp.hidden = opts.signedIn();
  };

  const refresh = (): void => {
    footer.refresh();
    refreshPlaySummary();
    refreshAgain();
    refreshSignUp();
    refreshActivity();
  };
  const show = (): void => {
    refresh();
    // Re-read on every return to the menu: a game just finished, or a claim just landed.
    opts.rank?.refresh();
    root.style.display = '';
    root.scrollTop = 0; // a return from a game lands on the hero, never mid-page
    foldReported = false; // …so a return is a fresh chance to reach the band, and reports one
  };
  const hide = (): void => {
    root.style.display = 'none';
  };
  void unsubscribeRank; // the home screen lives for the session; the handle is kept for symmetry

  return {
    root,
    show,
    hide,
    refresh,
    setFriendsBadge: (n) => footer.setFriendsBadge(n),
    refreshPlaySummary,
    refreshActivity,
  };
}
